import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';
import {
	IAdminTeamMember,
	invalidateTeamQueries,
	removeTeamMember,
	useAdminTeamMembersQuery,
} from '../../api/admin';
import { apiErrorMessage } from '../../utils/api';
import AddTeamMembersDialog from './AddTeamMembersDialog';
import AdminGridLink from './AdminGridLink';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	teamId: number;
	teamName: string;
}

export default function TeamMembersTable({ teamId, teamName }: Props) {
	const queryClient = useQueryClient();
	const { data: members, isLoading } = useAdminTeamMembersQuery(teamId);
	const [addOpen, setAddOpen] = useState(false);
	const [removeTarget, setRemoveTarget] = useState<IAdminTeamMember | null>(null);
	const [removing, setRemoving] = useState(false);
	const [removeError, setRemoveError] = useState<string | null>(null);

	async function handleRemoveConfirm() {
		if (!removeTarget) return;
		setRemoving(true);
		setRemoveError(null);
		try {
			await removeTeamMember(teamId, removeTarget.user_id);
			await invalidateTeamQueries(queryClient);
			setRemoveTarget(null);
		} catch (e: any) {
			setRemoveError(apiErrorMessage(e, 'Failed to remove user from team.'));
		} finally {
			setRemoving(false);
		}
	}

	const columns: GridColDef[] = [
		{
			field: 'full_name',
			headerName: 'Name',
			flex: 1,
			minWidth: 160,
			renderCell: (params: GridRenderCellParams) => (
				<AdminGridLink
					to={`/admin/users/${params.row.user_id}`}
					state={{ userEmail: params.row.email, userName: params.row.full_name }}
				>
					{params.value}
				</AdminGridLink>
			),
		},
		{ field: 'email', headerName: 'Email', flex: 1.2, minWidth: 200 },
		{ field: 'can_share', headerName: 'Can Share', width: 100, type: 'boolean' },
		{ field: 'can_receive', headerName: 'Can Receive', width: 110, type: 'boolean' },
		{ field: 'is_active', headerName: 'Active', width: 90, type: 'boolean' },
		{
			field: 'joined_at',
			headerName: 'Joined',
			width: 120,
			valueFormatter: ({ value }) => (value ? new Date(value).toLocaleDateString('en-CA') : ''),
		},
		{
			field: 'added_by_email',
			headerName: 'Added By',
			flex: 1,
			minWidth: 160,
		},
		{
			field: 'actions',
			headerName: '',
			width: 48,
			sortable: false,
			filterable: false,
			disableColumnMenu: true,
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title="Remove from team">
					<IconButton
						size="small"
						color="error"
						onClick={() => setRemoveTarget(params.row as IAdminTeamMember)}
					>
						<PersonRemoveIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			),
		},
	];

	return (
		<>
			<Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mb: 1 }}>
				<Button
					size="small"
					variant="contained"
					startIcon={<GroupAddIcon />}
					onClick={() => setAddOpen(true)}
				>
					Add Users
				</Button>
			</Stack>
			<Box sx={{ height: 'calc(100vh - 340px)', width: '100%' }}>
				<DataGrid
					rows={members ?? []}
					columns={columns}
					loading={isLoading}
					density="compact"
					disableSelectionOnClick
					components={{ Toolbar: AdminQuickFilterToolbar }}
					componentsProps={{ toolbar: { showQuickFilter: true } }}
				/>
			</Box>

			<AddTeamMembersDialog
				open={addOpen}
				onClose={() => setAddOpen(false)}
				teamId={teamId}
				teamName={teamName}
				existingMemberIds={(members ?? []).map((m) => m.user_id)}
			/>

			<Dialog
				open={!!removeTarget}
				onClose={() => !removing && setRemoveTarget(null)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { backgroundImage: 'none' } }}
			>
				<DialogTitle>Remove from Team</DialogTitle>
				<DialogContent>
					{removeError && (
						<Typography color="error" variant="body2" sx={{ mb: 1 }}>
							{removeError}
						</Typography>
					)}
					<Typography>
						Remove <strong>{removeTarget?.full_name ?? removeTarget?.email}</strong> from{' '}
						<strong>{teamName}</strong>?
					</Typography>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setRemoveTarget(null)} disabled={removing}>
						Cancel
					</Button>
					<Button
						variant="contained"
						color="error"
						onClick={handleRemoveConfirm}
						disabled={removing}
						startIcon={removing ? <CircularProgress size={16} /> : undefined}
					>
						{removing ? 'Removing…' : 'Remove'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}
